import React, { useState, useEffect, useRef } from 'react';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import { getApiUrl } from '../../../config/api';
import { getLocalConfigsList } from '../../../utils/configUtils';
import s from './UserStats.module.sass';

// Импорт иконок тарифов
import tarif1Icon from '../../../img/iconsas/1tarif.png';
import tarif2Icon from '../../../img/iconsas/2tarif.png';
import tarif3Icon from '../../../img/iconsas/3tarif.png';
import tarif4Icon from '../../../img/iconsas/4tarif.png';
import viptarifIcon from '../../../img/iconsas/viptarif.png';

// Палитра Nova для серий графиков (индиго, зелёный, голубой, янтарь, коралл...)
const COLORS = ['#7B7FF2', '#3ECF8E', '#4DA3FF', '#E2B203', '#F06A6A', '#B48CF2', '#5AD7C0', '#F0964D'];

// ── Календарь диапазона дат в стиле Nova ─────────────────────────
// Выбор двумя кликами (начало → конец) или протяжкой мыши,
// с превью диапазона под курсором, выходными и пресетами периодов.

const MONTHS_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONTHS_SHORT_RU = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const WEEKDAYS_RU = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

// ISO без UTC-сдвига (toISOString() уводит дату на день при ночных ТЗ)
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromISO = (str) => {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const formatRangeLabel = (startISO, endISO) => {
    if (!startISO || !endISO) return 'Выберите период';
    const a = fromISO(startISO);
    const b = fromISO(endISO);
    const left = a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
        ? String(a.getDate())
        : `${a.getDate()} ${MONTHS_SHORT_RU[a.getMonth()]}${a.getFullYear() !== b.getFullYear() ? ` ${a.getFullYear()}` : ''}`;
    return `${left} — ${b.getDate()} ${MONTHS_SHORT_RU[b.getMonth()]} ${b.getFullYear()}`;
};

const NovaRangeCalendar = ({ start, end, onChange, styles: s }) => {
    const [open, setOpen] = useState(false);
    const [view, setView] = useState(() => {
        const base = end ? fromISO(end) : new Date();
        return new Date(base.getFullYear(), base.getMonth(), 1);
    });
    // Незавершённый выбор: первая точка поставлена, ждём вторую
    const [pendingStart, setPendingStart] = useState(null);
    const [hoverISO, setHoverISO] = useState(null);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setPendingStart(null);
                setHoverISO(null);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    // Сетка: 6 недель с понедельника
    const offset = (new Date(view.getFullYear(), view.getMonth(), 1).getDay() + 6) % 7;
    const days = Array.from({ length: 42 }, (_, i) => new Date(view.getFullYear(), view.getMonth(), 1 - offset + i));
    const todayISO = toISO(new Date());

    // Пока идёт выбор — диапазон рисуется от первой точки до курсора
    const previewLo = pendingStart ? (hoverISO && hoverISO < pendingStart ? hoverISO : pendingStart) : start;
    const previewHi = pendingStart ? (hoverISO && hoverISO > pendingStart ? hoverISO : pendingStart) : end;

    const commit = (aISO, bISO) => {
        const [lo, hi] = aISO <= bISO ? [aISO, bISO] : [bISO, aISO];
        onChange(lo, hi);
        setPendingStart(null);
        setHoverISO(null);
        setOpen(false);
    };

    // Первое нажатие ставит начало; второе (клик или отпускание после
    // протяжки) фиксирует конец
    const handleDayDown = (iso) => {
        if (pendingStart) commit(pendingStart, iso);
        else {
            setPendingStart(iso);
            setHoverISO(iso);
        }
    };
    const handleDayUp = (iso) => {
        if (pendingStart && iso !== pendingStart) commit(pendingStart, iso);
    };

    const applyPreset = (daysBack) => {
        const endD = new Date();
        const startD = new Date();
        startD.setDate(endD.getDate() - daysBack + 1);
        commit(toISO(startD), toISO(endD));
    };
    const applyThisMonth = () => {
        const now = new Date();
        commit(toISO(new Date(now.getFullYear(), now.getMonth(), 1)), toISO(now));
    };
    // «Всё время»: с запасом раньше первых записей в базе
    const applyAllTime = () => {
        commit('2020-01-01', toISO(new Date()));
    };

    return (
        <div className={s.calWrap} ref={wrapRef}>
            <button
                type="button"
                className={`${s.calTrigger} ${open ? s.calTriggerOpen : ''}`}
                onClick={() => setOpen(!open)}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <span>{formatRangeLabel(start, end)}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points={open ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                </svg>
            </button>
            {open && (
                <div className={s.calPop}>
                    <div className={s.calPresets}>
                        <button type="button" onClick={() => applyPreset(7)}>7 дней</button>
                        <button type="button" onClick={() => applyPreset(14)}>14 дней</button>
                        <button type="button" onClick={() => applyPreset(30)}>30 дней</button>
                        <button type="button" onClick={applyThisMonth}>Этот месяц</button>
                        <button type="button" onClick={applyAllTime}>Всё время</button>
                    </div>
                    <div className={s.calHead}>
                        <button
                            type="button"
                            className={s.calNav}
                            onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                        </button>
                        <span className={s.calMonth}>{MONTHS_RU[view.getMonth()]} {view.getFullYear()}</span>
                        <button
                            type="button"
                            className={s.calNav}
                            onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </button>
                    </div>
                    <div className={s.calGrid}>
                        {WEEKDAYS_RU.map((w, i) => (
                            <span key={w} className={`${s.calWeekday} ${i >= 5 ? s.calWeekendHead : ''}`}>{w}</span>
                        ))}
                        {days.map((d) => {
                            const iso = toISO(d);
                            const wd = (d.getDay() + 6) % 7;
                            const inRange = previewLo && previewHi && iso >= previewLo && iso <= previewHi;
                            const cls = [
                                s.calDay,
                                d.getMonth() !== view.getMonth() ? s.calDayOther : '',
                                wd >= 5 ? s.calDayWeekend : '',
                                iso === todayISO ? s.calDayToday : '',
                                inRange ? s.calDayInRange : '',
                                iso === previewLo ? s.calDayEdgeStart : '',
                                iso === previewHi ? s.calDayEdgeEnd : '',
                            ].filter(Boolean).join(' ');
                            return (
                                <button
                                    key={iso}
                                    type="button"
                                    className={cls}
                                    onPointerDown={() => handleDayDown(iso)}
                                    onPointerUp={() => handleDayUp(iso)}
                                    onPointerEnter={() => { if (pendingStart) setHoverISO(iso); }}
                                >
                                    <span>{d.getDate()}</span>
                                </button>
                            );
                        })}
                    </div>
                    <div className={s.calHint}>
                        {pendingStart
                            ? 'Теперь кликните конец диапазона'
                            : 'Кликните начало и конец — или протяните мышью'}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Современные обёртки для recharts в стиле Nova ────────────────
// Общие принципы: без осевых линий и вертикальной сетки, целочисленные
// деления, мягкая пунктирная горизонталь, кастомный тултип-карточка.

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className={s.tipCard}>
            {label !== undefined && label !== '' && <div className={s.tipLabel}>{label}</div>}
            {payload.map((p, i) => (
                <div key={i} className={s.tipRow}>
                    <span className={s.tipDot} style={{ background: p.color || p.payload?.fill || '#7B7FF2' }} />
                    <span className={s.tipName}>{p.name}</span>
                    <span className={s.tipVal}>{p.value}</span>
                </div>
            ))}
        </div>
    );
};

// Линия с градиентной заливкой под ней (тренды по дням/часам)
const NovaArea = ({ data, xKey, color, name, gradId }) => (
    <ResponsiveContainer width="100%" height={240}>
        <AreaChart data={data} margin={{ top: 8, right: 10, left: -8, bottom: 0 }}>
            <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.32} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 6" />
            <XAxis dataKey={xKey} axisLine={false} tickLine={false} dy={6} minTickGap={26} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={36} />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(255, 255, 255, 0.18)', strokeDasharray: '3 3' }} />
            <Area
                type="monotone"
                dataKey="count"
                name={name}
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradId})`}
                dot={data.length <= 2 ? { r: 3.5, strokeWidth: 0, fill: color } : false}
                activeDot={{ r: 4.5, strokeWidth: 0 }}
            />
        </AreaChart>
    </ResponsiveContainer>
);

// Вертикальные бары для коротких категорий (статусы, часы, дни недели)
const NovaBars = ({ data, xKey = 'name', color, name, tick, xHeight }) => (
    <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 8, right: 10, left: -8, bottom: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 6" />
            <XAxis dataKey={xKey} axisLine={false} tickLine={false} dy={6} tick={tick} height={xHeight} interval={0} />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={36} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.04)' }} />
            <Bar
                dataKey="value"
                name={name}
                fill={color}
                radius={[6, 6, 0, 0]}
                maxBarSize={40}
                background={{ fill: 'rgba(255, 255, 255, 0.03)', radius: 6 }}
            />
        </BarChart>
    </ResponsiveContainer>
);

// Горизонтальные бары для длинных названий (конфиги, банки) —
// читаются без наклонных подписей, высота растёт по числу строк
const NovaHBars = ({ data, color, name }) => (
    <ResponsiveContainer width="100%" height={Math.max(200, data.length * 36 + 36)}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 14, left: 4, bottom: 0 }}>
            <CartesianGrid horizontal={false} strokeDasharray="3 6" />
            <XAxis type="number" allowDecimals={false} axisLine={false} tickLine={false} height={22} />
            <YAxis type="category" dataKey="name" width={140} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.04)' }} />
            <Bar
                dataKey="value"
                name={name}
                fill={color}
                radius={[0, 6, 6, 0]}
                maxBarSize={16}
                background={{ fill: 'rgba(255, 255, 255, 0.03)', radius: 6 }}
            />
        </BarChart>
    </ResponsiveContainer>
);

// Донат с итогом в центре и легендой с долями справа
const NovaDonut = ({ data }) => {
    const total = data.reduce((acc, item) => acc + (item.value || 0), 0);
    return (
        <div className={s.donutWrap}>
            <div className={s.donutChart}>
                <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={62}
                            outerRadius={88}
                            paddingAngle={data.length > 1 ? 2.5 : 0}
                            cornerRadius={5}
                            dataKey="value"
                            stroke="none"
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
                <div className={s.donutCenter}>
                    <span className={s.donutTotal}>{total}</span>
                    <span className={s.donutTotalLabel}>всего</span>
                </div>
            </div>
            <div className={s.donutLegend}>
                {data.map((entry, index) => (
                    <div key={entry.name} className={s.legendRow}>
                        <span className={s.legendDot} style={{ background: COLORS[index % COLORS.length] }} />
                        <span className={s.legendName}>{entry.name}</span>
                        <span className={s.legendVal}>
                            {total ? Math.round((entry.value / total) * 100) : 0}%
                            <em>{entry.value}</em>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ── Тарифы: свои цветовые маркеры вместо PNG-иконок ──────────────
// Каждому тарифу — свой цвет (тёплая прогрессия 1→VIP) и короткая
// метка (число или «VIP»), извлекаемые из названия тарифа
const TARIFF_STYLE = {
    '1': { color: '#3ECF8E', label: '1' },
    '2': { color: '#4DA3FF', label: '2' },
    '3': { color: '#7B7FF2', label: '3' },
    '4': { color: '#B48CF2', label: '4' },
    'VIP': { color: '#E7B008', label: 'VIP' },
};

const getTariffStyle = (raw) => {
    const norm = (raw || '').replace(/[💰❗⚠️\s]/g, '').toUpperCase();
    if (norm.includes('VIP') || norm.includes('ВИП')) return TARIFF_STYLE.VIP;
    const digit = (norm.match(/[1-4]/) || [])[0];
    if (digit && TARIFF_STYLE[digit]) return TARIFF_STYLE[digit];
    return { color: '#8B93A7', label: raw ? raw.slice(0, 3) : '—' };
};

// Вертикальные бары с индивидуальным цветом каждого тарифа. Метки —
// HTML-пилюлями под графиком (не SVG-тик), поэтому не обрезаются
// нижней кромкой карточки и всегда читаются
const NovaTariffBars = ({ data }) => {
    const styled = data.map(d => ({ ...d, st: getTariffStyle(d.name) }));
    return (
        <div className={s.tariffWrap}>
            <ResponsiveContainer width="100%" height={214}>
                <BarChart data={styled} margin={{ top: 8, right: 10, left: -8, bottom: 0 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 6" />
                    <XAxis dataKey="displayName" hide />
                    <YAxis allowDecimals={false} axisLine={false} tickLine={false} width={36} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.04)' }} />
                    <Bar dataKey="value" name="Пользователей" radius={[6, 6, 0, 0]} maxBarSize={44}
                        background={{ fill: 'rgba(255, 255, 255, 0.03)', radius: 6 }}>
                        {styled.map((entry, i) => (
                            <Cell key={i} fill={entry.st.color} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
            <div className={s.tariffLegend} style={{ paddingLeft: 28, paddingRight: 10 }}>
                {styled.map((entry, i) => (
                    <span
                        key={i}
                        className={s.tariffPill}
                        style={{ color: entry.st.color, background: `${entry.st.color}22`, borderColor: `${entry.st.color}66` }}
                    >
                        {entry.st.label}
                    </span>
                ))}
            </div>
        </div>
    );
};

const NoData = () => (
    <div className={s.noData}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <line x1="18" y1="20" x2="18" y2="10"></line>
            <line x1="12" y1="20" x2="12" y2="4"></line>
            <line x1="6" y1="20" x2="6" y2="14"></line>
        </svg>
        <span>Нет данных за период</span>
    </div>
);

const UserStats = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [availableConfigs, setAvailableConfigs] = useState([]);
    const [selectedConfigs, setSelectedConfigs] = useState([]);
    const [showConfigDropdown, setShowConfigDropdown] = useState(false);
    
    // По умолчанию показываем последние 7 дней
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const [startDate, setStartDate] = useState(sevenDaysAgo.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

    useEffect(() => {
        const loadConfigs = async () => {
            try {
                const configs = await getLocalConfigsList();
                setAvailableConfigs(configs);
            } catch (err) {
                console.error('Ошибка загрузки конфигов:', err);
            }
        };
        loadConfigs();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            // Проверяем, что клик был вне dropdown меню
            const dropdownMenu = e.target.closest('[data-dropdown-menu]');
            const dropdownButton = e.target.closest('[data-dropdown-button]');
            if (!dropdownMenu && !dropdownButton) {
                setShowConfigDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, []);

    const loadStats = async (retryCount = 0) => {
        if (!startDate || !endDate) return;
        
        setLoading(true);
        setError(null);
        
        const maxRetries = 3;
        const retryDelay = 1000; // 1 секунда
        
        try {
            const apiUrl = getApiUrl() || '';
            const apiPath = apiUrl ? `${apiUrl}/api/stats/users` : '/api/stats/users';
            let url = `${apiPath}?startDate=${startDate}T00:00:00.000Z&endDate=${endDate}T23:59:59.999Z`;
            
            // Добавляем фильтр по конфигам, если выбраны
            if (selectedConfigs.length > 0) {
                const configsParam = selectedConfigs.map(c => encodeURIComponent(c)).join(',');
                url += `&configs=${configsParam}`;
            }
            
            // Создаем AbortController для таймаута
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 секунд таймаут
            
            let response;
            try {
                response = await fetch(url, {
                    signal: controller.signal,
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                clearTimeout(timeoutId);
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === 'AbortError') {
                    throw new Error('Превышено время ожидания ответа сервера');
                }
                throw fetchError;
            }
            
            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Неизвестная ошибка');
                throw new Error(`Ошибка сервера (${response.status}): ${errorText}`);
            }
            
            const data = await response.json();
            if (data.success) {
                setStats(data.stats);
                setError(null); // Очищаем ошибку при успешной загрузке
            } else {
                throw new Error(data.error || 'Ошибка получения статистики');
            }
        } catch (err) {
            console.error('Ошибка загрузки статистики:', err);
            
            // Автоматический retry при сетевых ошибках или таймаутах
            if (retryCount < maxRetries && (
                err.message.includes('время ожидания') ||
                err.message.includes('Failed to fetch') ||
                err.message.includes('NetworkError') ||
                err.message.includes('Network request failed')
            )) {
                console.log(`Повторная попытка загрузки (${retryCount + 1}/${maxRetries})...`);
                setTimeout(() => {
                    loadStats(retryCount + 1);
                }, retryDelay * (retryCount + 1)); // Увеличиваем задержку с каждой попыткой
                return;
            }
            
            setError(err.message || 'Ошибка загрузки статистики');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStats(0); // Начинаем с retryCount = 0
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, selectedConfigs]);

    const handleConfigToggle = (configFilename) => {
        setSelectedConfigs(prev => {
            if (prev.includes(configFilename)) {
                return prev.filter(c => c !== configFilename);
            } else {
                return [...prev, configFilename];
            }
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('ru-RU', {
            day: '2-digit',
            month: '2-digit'
        }).format(date);
    };

    // Функция для получения иконки тарифа
    const getTariffIcon = (tariffLabel) => {
        if (!tariffLabel) return tarif1Icon;
        // Убираем эмодзи и пробелы для сравнения
        const normalized = tariffLabel.replace(/[💰❗⚠️\s]/g, '').toUpperCase();
        if (normalized.includes('1ТАРИФ') || normalized === '1') return tarif1Icon;
        if (normalized.includes('2ТАРИФ') || normalized === '2') return tarif2Icon;
        if (normalized.includes('3ТАРИФ') || normalized === '3') return tarif3Icon;
        if (normalized.includes('4ТАРИФ') || normalized === '4') return tarif4Icon;
        if (normalized.includes('VIP') || normalized.includes('ВИП')) return viptarifIcon;
        return tarif1Icon; // По умолчанию
    };

    // Функция для получения текста тарифа без эмодзи
    const getTariffText = (tariffLabel) => {
        if (!tariffLabel) return 'Без тарифа';
        // Убираем эмодзи, оставляем только текст
        return tariffLabel.replace(/[💰❗⚠️]/g, '').trim();
    };

    if (loading && !stats) {
        return (
            <div className={s.loading}>
                <div className={s.spinner}></div>
                <p>Загрузка статистики...</p>
            </div>
        );
    }

    if (error && !stats) {
        return (
            <div className={s.error}>
                <p>{error}</p>
                <button onClick={() => loadStats(0)} className={s.retryButton}>
                    <svg className={s.buttonIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 4 23 10 17 10"></polyline>
                        <polyline points="1 20 1 14 7 14"></polyline>
                        <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10"></path>
                        <path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14"></path>
                    </svg>
                    <span>Повторить</span>
                </button>
            </div>
        );
    }

    if (!stats) return null;

    // Подготовка данных для графиков
    const registrationsData = (stats.registrationsByDay || []).map(item => ({
        date: formatDate(item.date),
        count: item.count
    }));

    const emailsData = (stats.emailsByDay || []).map(item => ({
        date: formatDate(item.date),
        count: item.count
    }));

    const configData = (stats.registrationsByConfig || []).map(item => ({
        name: item.configFile?.replace('.json', '') || 'Без конфига',
        value: item.count
    }));

    const sapData = (stats.registrationsBySap || []).map(item => ({
        name: item.createdBy || 'Неизвестно',
        value: item.count
    }));

    const tariffData = (stats.registrationsByTariff || []).map(item => ({
        name: item.tariff || 'Без тарифа',
        displayName: getTariffText(item.tariff || 'Без тарифа'),
        icon: getTariffIcon(item.tariff || ''),
        value: item.count
    }));

    const emailTypeData = (stats.emailsByType || []).map(item => ({
        name: item.templateName || 'Без названия',
        value: item.count
    }));

    const emailStatusData = (stats.emailsByStatus || []).map(item => ({
        name: item.status === 'delivered' ? 'Доставлено' :
              item.status === 'pending' ? 'Ожидает' :
              item.status === 'failed' ? 'Ошибка' :
              item.status === 'bounced' ? 'Отклонено' :
              item.status === 'complained' ? 'Жалоба' :
              item.status || 'Неизвестно',
        value: item.count
    }));

    const emailConfigData = (stats.emailsByConfig || []).map(item => ({
        name: item.configFile?.replace('.json', '') || 'Без конфига',
        value: item.count
    }));

    // Новые данные для графиков
    const checkpointData = (stats.usersByCheckpoint || []).map(item => ({
        name: `Чекпоинт ${item.checkpoint}`,
        value: item.count
    }));

    const bankData = (stats.usersByBank || []).map(item => ({
        name: item.selectedBank || 'Без банка',
        value: item.count
    }));

    const registrationsHourData = Array.from({ length: 24 }, (_, i) => {
        const hourData = (stats.registrationsByHour || []).find(h => h.hour === i);
        return {
            hour: `${i}:00`,
            count: hourData?.count || 0
        };
    });

    const emailsHourData = Array.from({ length: 24 }, (_, i) => {
        const hourData = (stats.emailsByHour || []).find(h => h.hour === i);
        return {
            hour: `${i}:00`,
            count: hourData?.count || 0
        };
    });

    // Названия по индексу getDay() (0=Вс..6=Сб); неделю показываем с Пн
    const weekdayNames = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    const weekdayOrder = [1, 2, 3, 4, 5, 6, 0];
    const weekdayData = weekdayOrder.map((dayIndex) => {
        const dayData = (stats.registrationsByWeekday || []).find(d => d.weekday === dayIndex);
        return {
            name: weekdayNames[dayIndex],
            count: dayData?.count || 0
        };
    });

    const emailsBySapData = (stats.emailsBySap || []).map(item => ({
        name: item.sentBy || 'Неизвестно',
        value: item.count
    }));

    const renderChartIcon = (type) => {
        switch (type) {
            case 'line-registrations':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 17 9 11 13 15 21 7"></polyline>
                        <polyline points="14 7 21 7 21 14"></polyline>
                    </svg>
                );
            case 'line-emails':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2"></path>
                        <path d="M22 6v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6"></path>
                        <path d="m3 7 9 6 9-6"></path>
                    </svg>
                );
            case 'configs':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                );
            case 'saps':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H4a4 4 0 0 0-4 4v2"></path>
                        <circle cx="8" cy="7" r="4"></circle>
                        <line x1="20" y1="8" x2="20" y2="14"></line>
                        <line x1="23" y1="11" x2="17" y2="11"></line>
                    </svg>
                );
            case 'saps-mail':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H4a4 4 0 0 0-4 4v2"></path>
                        <circle cx="8" cy="7" r="4"></circle>
                        <path d="M23 7l-5 3-5-3"></path>
                        <rect x="13" y="5" width="10" height="8" rx="2"></rect>
                    </svg>
                );
            case 'tariffs':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 7h-9"></path>
                        <path d="M14 17H5"></path>
                        <circle cx="17" cy="17" r="3"></circle>
                        <circle cx="7" cy="7" r="3"></circle>
                    </svg>
                );
            case 'email-types':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16v16H4z"></path>
                        <line x1="4" y1="9" x2="20" y2="9"></line>
                        <line x1="9" y1="4" x2="9" y2="20"></line>
                    </svg>
                );
            case 'email-status':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="9 12 11 14 15 10"></polyline>
                    </svg>
                );
            case 'email-configs':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 6c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V6z"></path>
                        <path d="m6 7 8 5 8-5"></path>
                        <path d="M2 10h4"></path>
                        <path d="M2 14h4"></path>
                    </svg>
                );
            case 'checkpoints':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                );
            case 'banks':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 3 3 8h18L12 3z"></path>
                        <path d="M4 10h16"></path>
                        <path d="M6 10v8"></path>
                        <path d="M10 10v8"></path>
                        <path d="M14 10v8"></path>
                        <path d="M18 10v8"></path>
                        <path d="M2 18h20"></path>
                    </svg>
                );
            case 'hours':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                );
            case 'hours-mail':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                        <path d="M7 17h10"></path>
                    </svg>
                );
            case 'weekday':
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                );
            default:
                return (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="20" x2="18" y2="10"></line>
                        <line x1="12" y1="20" x2="12" y2="4"></line>
                        <line x1="6" y1="20" x2="6" y2="14"></line>
                    </svg>
                );
        }
    };

    const chartTitle = (label, type) => (
        <h3 className={s.chartTitle}>
            <span className={s.chartTitleIcon}>
                {renderChartIcon(type)}
            </span>
            <span>{label}</span>
        </h3>
    );

    return (
        <section className={s.panel}>
            <header className={s.panelHead}>
                <div className={s.panelHeadMain}>
                    <span className={s.panelIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 3v18h18"></path>
                            <path d="M18 17V9"></path>
                            <path d="M13 17V5"></path>
                            <path d="M8 17v-3"></path>
                        </svg>
                    </span>
                    <div className={s.panelHeadText}>
                        <h2 className={s.panelTitle}>Статистика пользователей</h2>
                        <p className={s.panelSub}>Сводка и графики за выбранный период</p>
                    </div>
                </div>
                <div className={s.controlsRow}>
                    <div className={s.dateControls}>
                        <label className={s.filterLabel}>
                                <span className={s.labelIcon}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                                        <line x1="16" y1="2" x2="16" y2="6"></line>
                                        <line x1="8" y1="2" x2="8" y2="6"></line>
                                        <line x1="3" y1="10" x2="21" y2="10"></line>
                                    </svg>
                                </span>
                            <span>Период:</span>
                            </label>
                        <NovaRangeCalendar
                            start={startDate}
                            end={endDate}
                            onChange={(a, b) => { setStartDate(a); setEndDate(b); }}
                            styles={s}
                        />
                    </div>
                    
                    <div className={s.configFilter}>
                        <label className={s.filterLabel}>
                            <span className={s.labelIcon}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                            </span>
                            <span>Конфиги:</span>
                        </label>
                        <div className={s.dropdownWrapper}>
                            <button
                                className={s.dropdownButton}
                                onClick={() => setShowConfigDropdown(!showConfigDropdown)}
                                data-dropdown-button
                            >
                                <span>
                                    {selectedConfigs.length === 0 
                                        ? 'Все конфиги' 
                                        : selectedConfigs.length === 1
                                            ? (availableConfigs.find(c => (c.filename || c.name || c) === selectedConfigs[0])?.name || selectedConfigs[0].replace('.json', ''))
                                            : `Выбрано: ${selectedConfigs.length}`
                                    }
                                </span>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points={showConfigDropdown ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
                                </svg>
                            </button>
                            {showConfigDropdown && (
                                <div className={s.dropdownMenu} data-dropdown-menu>
                                    <div className={s.dropdownHeader}>
                                        <span>Выберите конфиги</span>
                                        {selectedConfigs.length > 0 && (
                                            <button
                                                className={s.clearAllButton}
                                                onClick={() => setSelectedConfigs([])}
                                            >
                                                <svg className={s.buttonIcon} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                                </svg>
                                                <span>Очистить</span>
                                            </button>
                                        )}
                                    </div>
                                    <div className={s.checkboxList}>
                                        {availableConfigs.map((config) => {
                                            const configFilename = config.filename || config.name || config;
                                            const configName = config.name || configFilename.replace('.json', '') || configFilename;
                                            const isSelected = selectedConfigs.includes(configFilename);
                                            return (
                                                <label key={configFilename} className={s.checkboxItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleConfigToggle(configFilename)}
                                                    />
                                                    <span>{configName}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <button onClick={() => loadStats(0)} className={s.refreshButton} disabled={loading}>
                        <svg className={s.buttonIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="23 4 23 10 17 10"></polyline>
                            <polyline points="1 20 1 14 7 14"></polyline>
                            <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10"></path>
                            <path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14"></path>
                        </svg>
                        <span>{loading ? 'Загрузка...' : 'Обновить'}</span>
                    </button>
                </div>
            </header>

            <div className={s.panelBody}>
            {/* Общая статистика */}
            <div className={s.subGroup}>
                <div className={s.subLabel}>Ключевые показатели</div>
            <div className={s.summaryCards}>
                <div className={s.summaryCard}>
                    <div className={s.summaryLabel}>
                        <span className={s.summaryIcon}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                            </svg>
                        </span>
                        <span>Всего пользователей</span>
                    </div>
                    <div className={s.summaryValue}>{stats.totals.users}</div>
                </div>
                <div className={s.summaryCard}>
                    <div className={s.summaryLabel}>
                        <span className={s.summaryIcon}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2"></path>
                                <path d="M22 6v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6"></path>
                                <path d="m3 7 9 6 9-6"></path>
                            </svg>
                        </span>
                        <span>Всего писем</span>
                    </div>
                    <div className={s.summaryValue}>{stats.totals.emails}</div>
                </div>
                <div className={s.summaryCard}>
                    <div className={s.summaryLabel}>
                        <span className={s.summaryIcon}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                        </span>
                        <span>Средний чекпоинт</span>
                    </div>
                    <div className={s.summaryValue}>{stats.totals.avgCheckpoint || 0}</div>
                </div>
                <div className={s.summaryCard}>
                    <div className={s.summaryLabel}>
                        <span className={s.summaryIcon}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                        </span>
                        <span>Доставлено писем (конверсия)</span>
                    </div>
                    <div className={s.summaryValue}>{stats.totals.emailConversion || 0}%</div>
                </div>
                </div>
            </div>

            {/* Графики */}
            <div className={s.subGroup}>
                <div className={s.subLabel}>Графики</div>
            <div className={s.chartsGrid}>
                <div className={s.chartCard}>
                    {chartTitle('Регистрации по дням', 'line-registrations')}
                    <NovaArea data={registrationsData} xKey="date" color="#7B7FF2" name="Регистраций" gradId="gradReg" />
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Отправленные письма по дням', 'line-emails')}
                    <NovaArea data={emailsData} xKey="date" color="#3ECF8E" name="Писем" gradId="gradMail" />
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Регистрации по конфигам', 'configs')}
                    {configData.length > 0 ? (
                        <NovaHBars data={configData} color="#7B7FF2" name="Пользователей" />
                    ) : (
                        <NoData />
                    )}
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Регистрации по саппам', 'saps')}
                    {sapData.length > 0 ? <NovaDonut data={sapData} /> : <NoData />}
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Регистрации по тарифам', 'tariffs')}
                    {tariffData.length > 0 ? (
                        <NovaTariffBars data={tariffData} />
                    ) : (
                        <NoData />
                    )}
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Письма по типам', 'email-types')}
                    {emailTypeData.length > 0 ? <NovaDonut data={emailTypeData} /> : <NoData />}
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Статусы писем', 'email-status')}
                    {emailStatusData.length > 0 ? (
                        <NovaBars data={emailStatusData} color="#E2B203" name="Писем" />
                    ) : (
                        <NoData />
                    )}
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Письма по конфигам', 'email-configs')}
                    {emailConfigData.length > 0 ? (
                        <NovaHBars data={emailConfigData} color="#F06A6A" name="Писем" />
                    ) : (
                        <NoData />
                    )}
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Распределение по чекпоинтам', 'checkpoints')}
                    {checkpointData.length > 0 ? (
                        <NovaBars data={checkpointData} color="#B48CF2" name="Пользователей" />
                    ) : (
                        <NoData />
                    )}
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Выбор банков', 'banks')}
                    {bankData.length > 0 ? (
                        <NovaHBars data={bankData} color="#F0964D" name="Пользователей" />
                    ) : (
                        <NoData />
                    )}
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Регистрации по часам', 'hours')}
                    <NovaArea data={registrationsHourData} xKey="hour" color="#4DA3FF" name="Регистраций" gradId="gradHourReg" />
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Письма по часам', 'hours-mail')}
                    <NovaArea data={emailsHourData} xKey="hour" color="#5AD7C0" name="Писем" gradId="gradHourMail" />
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Активность по дням недели', 'weekday')}
                    {weekdayData.length > 0 ? (
                        <NovaBars data={weekdayData.map(d => ({ name: d.name, value: d.count }))} color="#8B93A7" name="Регистраций" />
                    ) : (
                        <NoData />
                    )}
                </div>

                <div className={s.chartCard}>
                    {chartTitle('Саппы, отправившие письма', 'saps-mail')}
                    {emailsBySapData.length > 0 ? <NovaDonut data={emailsBySapData} /> : <NoData />}
                </div>
            </div>
        </div>
            </div>
        </section>
    );
};

export default UserStats;
